<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import { ref, onMounted } from "vue";
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useToast } from 'vue-toast-notification';
import Loading from 'vue-loading-overlay';
import PackageSubMenu from '@/Includes/PackageSubMenu.vue';

const props = defineProps({
    year: String,
    packagesById: Object,
    flash: Object,
    user: Object,
});

let isLoading = ref(false);
dayjs.extend(advancedFormat);
let menu = 'details';
const $toast = useToast();

onMounted(() => {
    if (props.flash.message) {
        $toast.open({
            message: props.flash.message,
            type: "info",
            duration: 4000,
            position: 'top-right',
            class: 'bs-toast toast bg-dark show'
        });
    }
});

function formatPrice(value) {
    let val = (value / 1).toFixed(2).replace('.', '.');
    return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
</script>
<style scoped>
@import "../../../../public/ui/vendor/libs/toastr/toastr.css";
@import "vue-loading-overlay/dist/vue-loading.css";
</style>
<template>
    <AppLayout title="Booking Details" :year=year :user=user>
        <div class="vld-parent">
            <Loading v-model:active="isLoading" :color="'#696cff'" />
        </div>
        <div class="container-xxl flex-grow-1 container-p-y">
            <PackageSubMenu :menu="menu" :packagesById="packagesById" />
            <div class="row">
                <div class="col-xl-12 col-lg-12 col-md-12">
                    <div class="card mb-4">
                        <div class="card-body">
                            <h5 class="card-title text-primary">Package Details</h5>
                            <div class="row g-3">
                                <span class="col-md-4">
                                    <ul class="list-unstyled mb-4 mt-3">
                                        <li class="d-flex align-items-center mb-3">
                                            <i class="bx bx-chevron-right"></i><span class="fw-bold mx-2 ">Hotel
                                                Name:</span>
                                            <span>{{ packagesById.hotel.name }}</span>
                                        </li>
                                        <li v-if="packagesById.packagerooms.length > 1"
                                            class="d-flex align-items-center mb-3">
                                            <i class="bx bx-chevron-right"></i><span class="fw-bold mx-2 ">Stay
                                                Type:</span>
                                            <span>Split Stay</span>
                                        </li>
                                        <li v-else class="d-flex align-items-center mb-3">
                                            <i class="bx bx-chevron-right"></i><span class="fw-bold mx-2 ">Stay
                                                Type:</span>
                                            <span>Full Stay</span>
                                        </li>
                                        <li v-for="roomtype in packagesById.packagerooms" :key="roomtype.id"
                                            class="d-flex align-items-center mb-3">
                                            <i class="bx bx-chevron-right"></i>
                                            <span class="fw-bold mx-2 ">Room
                                                Type:</span>
                                            <span>{{ roomtype.room_category?.name }} <b>For</b> {{
                                                roomtype.nights
                                            }}
                                                Nights</span>
                                        </li>
                                        <li class="d-flex align-items-center mb-3">
                                            <i class="bx bx-chevron-right"></i><span class="fw-bold mx-2 ">Meal
                                                Type:</span>
                                            <span>{{ packagesById.meal_type }}</span>
                                        </li>
                                        <li class="d-flex align-items-center mb-3">
                                            <i class="bx bx-chevron-right"></i><span class="fw-bold mx-2 ">Offer
                                                Code:</span>
                                            <span>{{ packagesById.offer_code }}</span>
                                        </li>
                                    </ul>
                                </span>
                                <span class="col-md-4">
                                    <ul class="list-unstyled mb-4 mt-3">
                                        <li class="d-flex align-items-center mb-3">
                                            <i class="bx bx-chevron-right"></i><span class="fw-bold mx-2 ">Booking
                                                From:</span>
                                            <span>{{
                                                dayjs(packagesById.booking_date_from).format('Do MMMM YYYY')
                                            }} <b>to</b>
                                                {{
                                                    dayjs(packagesById.booking_date_to).format('Do MMMM YYYY')
                                                }}</span>
                                        </li>
                                        <li class="d-flex align-items-center mb-3">
                                            <i class="bx bx-chevron-right"></i><span class="fw-bold mx-2 ">Travel
                                                From:</span>
                                            <span>{{
                                                dayjs(packagesById.travel_date_from).format('Do MMMM YYYY')
                                            }} <b>to</b>
                                                {{
                                                    dayjs(packagesById.travel_date_to).format('Do MMMM YYYY')
                                                }}</span>
                                        </li>
                                        <li class="d-flex align-items-center mb-3">
                                            <i class="bx bx-chevron-right"></i><span class="fw-bold mx-2 ">Transfer
                                                by:</span>
                                            <span>{{ packagesById.transfers[0]?.vessel }}</span>
                                        </li>
                                        <li class="d-flex align-items-center mb-3">
                                            <i class="bx bx-chevron-right"></i><span class="fw-bold mx-2 ">Allowed
                                                Countries:</span>
                                            <span>
                                                <span v-for="country in packagesById.countries">
                                                    {{ country.name }}, &nbsp;
                                                </span>
                                            </span>
                                        </li>
                                        <li class="d-flex align-items-center mb-3">
                                            <i class="bx bx-chevron-right"></i><span class="fw-bold mx-2 ">Number of
                                                Rooms:</span>
                                            <span>{{ packagesById.no_rooms }} Room</span>
                                        </li>

                                    </ul>
                                </span>

                                <span class="col-md-4">
                                    <ul class="list-unstyled mb-4 mt-3">
                                        <li class="d-flex align-items-center mb-3">
                                            <i class="bx bx-chevron-right"></i><span class="fw-bold mx-2 ">Adult
                                                Rate:</span>
                                            <span>$ {{ formatPrice(packagesById.adult_rate) }}</span>
                                        </li>
                                        <li class="d-flex align-items-center mb-3">
                                            <i class="bx bx-chevron-right"></i><span class="fw-bold mx-2 ">Child
                                                Rate:</span>
                                            <span>$ {{ formatPrice(packagesById.child_rate) }}</span>
                                        </li>
                                        <li class="d-flex align-items-center mb-3">
                                            <i class="bx bx-chevron-right"></i><span class="fw-bold mx-2 ">Infant
                                                Rate:</span>
                                            <span>$ {{ formatPrice(packagesById.infant_rate) }}</span>
                                        </li>
                                        <li class="d-flex align-items-center mb-3">
                                            <i class="bx bx-chevron-right"></i><span class="fw-bold mx-2 ">Adult Max
                                                Occupancy:</span>
                                            <span>{{ packagesById.adult_occupancy }} Adults</span>
                                        </li>
                                        <li class="d-flex align-items-center mb-3">
                                            <i class="bx bx-chevron-right"></i><span class="fw-bold mx-2 ">Child Max
                                                Occupancy:</span>
                                            <span>{{ packagesById.child_occupancy }} Children</span>
                                        </li>
                                        <li class="d-flex align-items-center mb-3">
                                            <i class="bx bx-chevron-right"></i><span class="fw-bold mx-2 ">Infant Max
                                                Occupancy:</span>
                                            <span>{{ packagesById.infant_occupancy }} Infants</span>
                                        </li>
                                    </ul>
                                </span>

                                <div class="alert alert-info d-flex mb-0" role="alert">
                                    <div class="d-flex flex-column ps-1">
                                        <h6 class="alert-heading d-flex align-items-center fw-bold mb-1 text-secondary">
                                            Description
                                        </h6>
                                        <span class="text-secondary" v-html="packagesById.description"></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>